

































































































import { defineComponent, PropType, computed, ref } from '@nuxtjs/composition-api';
import ProductDetailBarGraph from '~/components/pages/Product/Detail/ProductDetailBarGraph.vue';
import ProductDetailPopover from '~/components/pages/Product/Detail/ProductDetailPopover.vue';
import ProductStyleLabel from '~/components/pages/Product/ProductStyleLabel.vue';
import ProductWineColorLabel from '~/components/pages/Product/ProductWineColorLabel.vue';
import WineStyleInfo from '~/components/pages/Product/WineStyleInfo.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppModal from '~/components/partials/App/AppModal.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import { SPARKLING_ADDITIONAL_LIST, STYLE_INFO_LIST } from '~/constants';
import { BODY_NAME } from '~/constants/body';
import { url } from '~/utils';
import { formatKeyValueArray, getProductColorFromType } from '~/utils/modules/products';
import { ProductShowECResponseSchema } from '~/types/modules/product';
import { BooleanLike } from '~/enums';

export default defineComponent({
  components: {
    ProductDetailBarGraph,
    AppRectButton,
    ProductDetailPopover,
    AppImage,
    AppModal,
    WineStyleInfo,
    AppIcon,
    ProductStyleLabel,
    ProductWineColorLabel,
  },
  props: {
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      required: true,
    },
    isWineCategory: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isStyleInfoOpen = ref(false);
    const closeStyleInfoModal = () => {
      isStyleInfoOpen.value = false;
    };
    const data = computed(() => {
      const { attributes, master_settings } = props.ecResponse;
      const formattedAttributes = formatKeyValueArray(attributes);
      return {
        sweetnessPoint: Number(formattedAttributes?.sweetness || 0),
        color: getProductColorFromType(master_settings?.color?.name || ''),
        colorName: master_settings?.color?.name || '',
        colorId: Number(master_settings?.color?.id || 0),
        bodyPoint: Number(formattedAttributes?.taste_gauge || 0),
        senseType: Number(formattedAttributes?.sense_type || 0),
        styleId: Number(master_settings?.style?.id || 0),
        tastes: [
          { value: Number(formattedAttributes?.taste_fruity || 0), label: '果実味' },
          { value: Number(formattedAttributes?.taste_sour || 0), label: '酸味' },
          { value: Number(formattedAttributes?.taste_bitter || 0), label: '渋味' },
          { value: Number(formattedAttributes?.taste_robust || 0), label: 'コク' },
        ]
          .filter((taste) => taste.value > 0)
          .sort((a, b) => a.value - b.value)
          .slice(0, 3),
      };
    });

    const isShownTasteChart = computed(() => {
      return formatKeyValueArray(props.ecResponse.attributes)?.taste_chart_display_flag === 'true';
    });

    /**
     * 「同じ味わいのワインを探す」ボタンのリンク(絞り込み条件)生成
     * ※商品情報にタイプ・スタイルが含まれていない場合はリンクを生成しない=ボタンをトルツメする
     */
    const listPath = computed(() => {
      if (!data.value.colorId || !data.value.styleId) return;
      return {
        path: url('ITEM_LIST'),
        query: { _color: data.value.colorId, _style: data.value.styleId, _qty: BooleanLike.TRUE },
      };
    });

    const isShownTasteSecion = computed(() => isShownTasteChart.value || listPath.value);

    const hasContent = computed(() => data.value.sweetnessPoint || data.value.bodyPoint || isShownTasteSecion.value);
    const aromas = computed(() => props.ecResponse.master_settings?.aromas || []);
    /**
     * 香りの登録数(最大5件)と登録順を組み合わせ、表示位置のcssを割り当てる
     * ※大きさもcssで割り振るため、widthは100%固定にしています
     */
    const positionClass = (index: number) => {
      return `position${aromas.value.length}-${index}`;
    };

    const textColor = computed(() => {
      switch (data.value.color) {
        case 'WineRed':
          return 'text-WineRed';
        case 'WineWhite':
          return 'text-WineWhite';
        case 'WineRose':
          return 'text-WineRose';
        case 'WineOrange':
          return 'text-WineOrange';
        case 'WineSparkling':
          return 'text-WineSparkling';
      }
    });
    /**
     * 現在のスタイルと色の組み合わせから、スタイル情報を取得する
     * ※スタイルがない場合は最も色の薄いスタイル情報(先頭に登録されている情報)を表示する
     */
    const style = computed(() => {
      const styles = STYLE_INFO_LIST.find(({ colorId }) => colorId === data.value.colorId.toString())?.styles || [];
      return styles?.find((style) => style.style_id === data.value.styleId) || styles[0];
    });
    // 型が違うため、通常のスタイルとは別途定義する
    const sparklingStyle = computed(() => {
      const styles =
        SPARKLING_ADDITIONAL_LIST.find(({ colorId }) => colorId === data.value.colorId.toString())?.styles || [];
      return styles?.find((style) => style.style_id === data.value.styleId) || styles[0];
    });

    const currentStyle = computed(() => sparklingStyle.value ?? style.value);

    const barColor = computed(() => {
      if (sparklingStyle.value && data.value.styleId) {
        // ボディバー専用の色が設定されている場合は、そのプロパティを返す
        return sparklingStyle.value.bg_bar_color_class;
      } else if (style.value && data.value.styleId) {
        return style.value.bg_color_class;
      }
      return data.value.color;
    });
    return {
      isStyleInfoOpen,
      closeStyleInfoModal,
      BODY_NAME,
      data,
      isShownTasteChart,
      listPath,
      isShownTasteSecion,
      hasContent,
      aromas,
      positionClass,
      textColor,
      currentStyle,
      barColor,
    };
  },
});
